import CloseIcon from "@mui/icons-material/Close";
import NightlightIcon from "@mui/icons-material/Nightlight";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, Divider, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { SHIFT_NIGHT_FALLBACK } from "../../theme/tokens";
import { NotificationType, type NurseFullRole, type OpenShiftRequest, type ReplaceShiftRequest, ShiftType } from "../../utils/models";
import { getDaysUntilShift } from "../../utils/nursePanelUtils";
import { getRoleGroups } from "../../utils/roles";
import { DateRangleLabel } from "../DateRangeLabel/DateRangeLabel";
import ColorfulRole from "../UIToolkit/ColorfulRole/ColorfulRole";

interface IProps {
	request: OpenShiftRequest | ReplaceShiftRequest;
}

export const ShiftDataHeader = ({ request }: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();
	const { getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const jobName = jobManagementEnabled && request.job_id ? chosenDepartment.jobs?.find((j) => j.id === request.job_id)?.displayName : null;

	const title: string = useMemo(() => {
		switch (request.request_type) {
			case NotificationType.OpenShift:
				return t("approve.shift.requests");
			case NotificationType.ReplaceShift:
				return t("approve.shift.replace");
			case NotificationType.ReplaceAgency:
				return t("approve.shift.replace.agency");
			default:
				return t("approve.shift.requests");
		}
	}, [request, t]);

	const postedAgoLabel = (): string => {
		const dayDiff = getDaysUntilShift(request.message.date);

		if (dayDiff === 0) {
			return t("shift.is.today");
		}
		if (dayDiff === 1) {
			return t("shift.is.tomorrow");
		}
		return t("shift.in.days", { count: dayDiff });
	};

	const timeAgoLabel = (): string => {
		const hoursDiff = Math.floor(DateTime.now().diff(request.created_at, "hour").hours);

		if (hoursDiff >= 24) {
			const daysDiff = Math.floor(hoursDiff / 24);
			return daysDiff === 1 ? t("posted.day.ago", { count: daysDiff }) : t("posted.days.ago", { count: daysDiff });
		}
		return hoursDiff === 1 ? t("posted.hour.ago", { count: hoursDiff }) : t("posted.hours.ago", { count: hoursDiff });
	};

	return (
		<Box
			sx={{
				backgroundColor: "white",
				padding: 2,
				marginBottom: 1,
				boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography sx={{ fontSize: "20px", color: "primary.main", fontWeight: 600 }}>{title}</Typography>
				<CloseIcon
					onClick={() => navigate(`/${chosenDepartment.department_id}/staffing/${request.message.shift_start.toFormat("yyyy-MM-dd")}`)}
					sx={{ color: "primary.main" }}
				/>
			</Box>
			<Divider sx={{ margin: "5px" }} />
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
						<Box>
							<Box>
								{jobManagementEnabled && jobName ? (
									<Typography sx={{ fontWeight: 600, color: "primary.dark", fontSize: "14px" }}>{jobName}</Typography>
								) : (
									getRoleGroups(request.message.roles as NurseFullRole[], chosenDepartment).map((roleGroupName) => (
										<Box
											key={roleGroupName.charAt(0).toUpperCase() + roleGroupName.slice(1)}
											sx={{
												display: "flex",
												alignItems: "center",
											}}
										>
											<ColorfulRole roleGroup={roleGroupName.charAt(0).toUpperCase() + roleGroupName.slice(1)} />
										</Box>
									))
								)}
							</Box>
						</Box>
						<Typography sx={{ color: "primary.main" }}> ({(request as OpenShiftRequest).needed_nurses}) </Typography>
						{request.message.shift_type === ShiftType.Day && <WbSunnyIcon sx={{ color: "#FBBB00", fontSize: 14 }} />}
						{request.message.shift_type === ShiftType.Night && <NightlightIcon sx={{ color: SHIFT_NIGHT_FALLBACK, fontSize: 14 }} />}
					</Box>

					{(request.request_type === NotificationType.ReplaceShift || request.request_type === NotificationType.ReplaceAgency) && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								color: "primary.dark",
								ml: "auto",
							}}
						>
							<Typography sx={{ fontSize: "14px" }}>{getNurseFormattedName((request as ReplaceShiftRequest).replacement_nurse_id)}</Typography>
							<SwapHorizIcon sx={{ fontSize: 20, ml: 0.5 }} />
						</Box>
					)}
				</Box>
				<DateRangleLabel
					startTime={request.message.shift_start}
					endTime={request.message.shift_end}
					dayStart={chosenDepartment.dayStart}
					graceMinutes={chosenDepartment.scheduled_shift_delta_minutes}
				/>
				{request.location && <Typography sx={{ fontSize: "14px", color: "primary.dark", ml: "4px" }}>{request.location}</Typography>}
				<Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
					<Typography
						sx={{
							fontSize: "13px",
							color: "primary.dark",
							opacity: "50%",
							marginLeft: "4px",
						}}
					>
						{timeAgoLabel()}
					</Typography>
					<Typography variant="body2" sx={{ color: "#e74c3c", fontWeight: 500, fontSize: "14px" }}>
						{postedAgoLabel()}
					</Typography>
				</Box>
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
				{(() => {
					const displayBonus = request.current_bonus ?? request.bonus;
					return (
						!!displayBonus && (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
									bgcolor: "var(--inhouse-secondary)",
									borderRadius: "16px",
									px: 1.5,
									alignSelf: "center",
								}}
							>
								<img src="/bonus.svg" alt="bonus" width={12} />
								<Typography sx={{ color: "white", fontWeight: "bold", fontSize: "0.875rem" }}>+${displayBonus} pickup bonus</Typography>
							</Box>
						)
					);
				})()}
			</Box>
		</Box>
	);
};
